import { useState, useRef, useCallback, useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { sfxClick, sfxHover } from '@/lib/soundFX';
import { useIsMobile } from '@/hooks/useMobile';

// ── Department filter config ──────────────────────────────────────────────────
const DEPT_FILTERS = [
  { id: 'all',              label: 'ALL',     color: '#00A6EB' },
  { id: 'executive',        label: 'EXEC',    color: '#A855F7' },
  { id: 'sales-marketing',  label: 'SALES',   color: '#FF0055' },
  { id: 'tech-engineering', label: 'TECH',    color: '#00FF88' },
  { id: 'finance',          label: 'FIN',     color: '#FFB800' },
  { id: 'hr-talent',        label: 'HR',      color: '#00A6EB' },
  { id: 'legal-compliance', label: 'LEGAL',   color: '#FF6B35' },
  { id: 'data-analytics',   label: 'DATA',    color: '#00FFFF' },
  { id: 'customer-success', label: 'CX',      color: '#FF0055' },
  { id: 'ops-logistics',    label: 'OPS',     color: '#A855F7' },
  { id: 'creative-design',  label: 'DESIGN',  color: '#FFB800' },
];

function CornerBracket({ pos }: { pos: 'tl' | 'tr' | 'bl' | 'br' }) {
  const size = 28;
  const base: React.CSSProperties = { position: 'absolute', width: size, height: size, zIndex: 10, animation: 'cornerPulse 3s ease-in-out infinite' };
  const borders: Record<string, React.CSSProperties> = {
    tl: { top: -1, left: -1, borderTop: '2px solid #00A6EB', borderLeft: '2px solid #00A6EB' },
    tr: { top: -1, right: -1, borderTop: '2px solid #00A6EB', borderRight: '2px solid #00A6EB' },
    bl: { bottom: -1, left: -1, borderBottom: '2px solid #00A6EB', borderLeft: '2px solid #00A6EB' },
    br: { bottom: -1, right: -1, borderBottom: '2px solid #00A6EB', borderRight: '2px solid #00A6EB' },
  };
  return <div style={{ ...base, ...borders[pos], boxShadow: '0 0 8px rgba(0,166,235,0.8)' }} />;
}

function DataStrip({ value, label, color = '#00A6EB' }: { value: string; label: string; color?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 60 }}>
      <div style={{ fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: '0.7rem', color, textShadow: `0 0 10px ${color}80`, letterSpacing: '0.08em' }}>{value}</div>
      <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.38rem', color: `${color}70`, letterSpacing: '0.15em' }}>{label}</div>
    </div>
  );
}

export default function HoloVideoPlayer() {
  // ── Live data ────────────────────────────────────────────────────────────────
  const { data: stats } = trpc.media.stats.useQuery(undefined, { staleTime: 60_000 });
  const { data: allVideos = [] } = trpc.media.carouselVideos.useQuery();

  // ── State ────────────────────────────────────────────────────────────────────
  const [activeDept, setActiveDept] = useState<string>('all');
  const [currentIdx, setCurrentIdx] = useState(0);
  const [muted, setMutedState] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [scanPos, setScanPos] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const expandedRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMobile = useIsMobile();

  // ── Filtered videos ──────────────────────────────────────────────────────────
  const videos = activeDept === 'all'
    ? allVideos
    : allVideos.filter((v: any) => v.department_id === activeDept || v.department_id === 'all');

  const agentCount = stats?.agentCount ?? 232;
  const videoCount = stats?.videoCount ?? allVideos.length;

  // Reset idx when filter changes or data loads
  useEffect(() => { setCurrentIdx(0); }, [activeDept, allVideos.length]);

  // Scan line animation
  useEffect(() => {
    const t = setInterval(() => setScanPos(p => (p + 1) % 100), 30);
    return () => clearInterval(t);
  }, []);

  // Auto-advance every 90s
  useEffect(() => {
    if (videos.length === 0) return;
    timerRef.current = setInterval(() => setCurrentIdx(p => (p + 1) % videos.length), 90000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [videos.length, activeDept]);

  // Sync muted
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
    if (expandedRef.current) expandedRef.current.muted = muted;
  }, [muted]);

  const currentVideo = videos[currentIdx] as any;
  const activeDeptConfig = DEPT_FILTERS.find(d => d.id === activeDept) ?? DEPT_FILTERS[0];

  const goNext = useCallback(() => {
    sfxClick();
    setCurrentIdx(p => (p + 1) % Math.max(videos.length, 1));
    if (timerRef.current) clearInterval(timerRef.current);
  }, [videos.length]);

  const goPrev = useCallback(() => {
    sfxClick();
    setCurrentIdx(p => (p - 1 + Math.max(videos.length, 1)) % Math.max(videos.length, 1));
    if (timerRef.current) clearInterval(timerRef.current);
  }, [videos.length]);

  const toggleMute = useCallback(() => { sfxClick(); setMutedState(p => !p); }, []);

  // On mobile: close expanded modal if it was somehow opened (safety guard)
  useEffect(() => {
    if (isMobile && isExpanded) setIsExpanded(false);
  }, [isMobile, isExpanded]);

  if (allVideos.length === 0) return null;

  const accentColor = activeDeptConfig.color;

  return (
    <>
      <style>{`
        @keyframes holoFlicker { 0%,95%,100%{opacity:1}96%{opacity:.85}97%{opacity:1}98%{opacity:.9} }
        @keyframes cornerPulse { 0%,100%{box-shadow:0 0 8px rgba(0,166,235,.8)}50%{box-shadow:0 0 18px rgba(0,166,235,1),0 0 30px rgba(0,166,235,.5)} }
        @keyframes dataScroll { 0%{transform:translateX(0)}100%{transform:translateX(-50%)} }
        @keyframes holoGlow {
          0%,100%{box-shadow:0 0 40px rgba(0,166,235,.25),0 0 80px rgba(0,166,235,.1),inset 0 0 40px rgba(0,166,235,.04)}
          50%{box-shadow:0 0 60px rgba(0,166,235,.4),0 0 120px rgba(0,166,235,.18),inset 0 0 60px rgba(0,166,235,.07)}
        }
        @keyframes sidePulse { 0%,100%{opacity:.4}50%{opacity:1} }
        .holo-dept-btn { transition: all 0.18s cubic-bezier(0.23,1,0.32,1); }
        .holo-dept-btn:hover { transform: translateY(-1px); }
        .holo-ctrl-btn { transition: all 0.15s ease; }
        .holo-ctrl-btn:hover { opacity: 0.85; transform: scale(0.97); }
        .holo-ctrl-btn:active { transform: scale(0.93); }
        @media (max-width: 900px) {
          .holo-side-panel { display: none !important; }
          .holo-dept-row { display: none !important; }
          .holo-dot-row { display: none !important; }
          .holo-nav-btns { display: none !important; }
        }
        @keyframes muteBtnPulse { 0%,100%{opacity:0.8} 50%{opacity:1} }
        @media (max-width: 900px) {
          .holo-outer-wrap {
            padding: 0 !important;
            margin: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .holo-frame {
            border-radius: 0 !important;
            clip-path: none !important;
            border-left: none !important;
            border-right: none !important;
            width: 100% !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
          }
        }
      `}</style>

      {/* ── OUTER WRAPPER — responsive max-width ─────────────────────────────── */}
      <div className="holo-outer-wrap" style={{ position: 'relative', width: '100%', maxWidth: isMobile ? '100%' : 1100, margin: '0 auto', padding: isMobile ? '0' : '0 1rem', boxSizing: 'border-box' }}>

        {/* ── HOLOGRAPHIC FRAME ─────────────────────────────────────────────── */}
        <div className="holo-frame" style={{
          position: 'relative',
          background: 'rgba(4,8,20,0.93)',
          border: `1px solid ${accentColor}55`,
          animation: 'holoGlow 4s ease-in-out infinite, holoFlicker 8s ease-in-out infinite',
          clipPath: isMobile ? 'none' : 'polygon(20px 0%,100% 0%,100% calc(100% - 20px),calc(100% - 20px) 100%,0% 100%,0% 20px)',
          transition: 'border-color 0.4s ease',
          overflow: 'hidden',
          boxSizing: 'border-box',
          width: '100%',
        }}>
          <CornerBracket pos="tl" />
          <CornerBracket pos="tr" />
          <CornerBracket pos="bl" />
          <CornerBracket pos="br" />

          {/* ── TOP HUD BAR ─────────────────────────────────────────────────── */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '6px 16px',
            background: `linear-gradient(90deg, ${accentColor}18 0%, ${accentColor}06 50%, ${accentColor}18 100%)`,
            borderBottom: `1px solid ${accentColor}30`,
            flexWrap: 'wrap', gap: 4,
          }}>
            {/* Left: status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00FF88', boxShadow: '0 0 8px #00FF88', animation: 'sidePulse 2s ease-in-out infinite' }} />
              <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.5rem', color: '#00FF88', letterSpacing: '0.2em' }}>INTEL FEED — LIVE</span>
              <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.42rem', color: `${accentColor}80`, letterSpacing: '0.12em' }}>
                {videos.length > 0 ? `STREAM ${currentIdx + 1}/${videos.length}` : 'NO STREAMS'}
              </span>
            </div>
            {/* Center: scrolling ticker (hidden on very small screens) */}
            <div style={{ flex: 1, overflow: 'hidden', margin: '0 12px', height: 14, minWidth: 0 }} className="holo-ticker-wrap">
              <div style={{ display: 'inline-block', whiteSpace: 'nowrap', animation: 'dataScroll 18s linear infinite', fontFamily: 'Share Tech Mono, monospace', fontSize: '0.4rem', color: `${accentColor}55`, letterSpacing: '0.1em' }}>
                ◈ {agentCount} AI AGENTS ACTIVE ◈ {videoCount} INTEL STREAMS ◈ ZERO SALARY OVERHEAD ◈ DEPLOY IN MINUTES ◈ SCALE INFINITELY ◈ {agentCount} AI AGENTS ACTIVE ◈ {videoCount} INTEL STREAMS ◈ ZERO SALARY OVERHEAD ◈ DEPLOY IN MINUTES ◈ SCALE INFINITELY ◈
              </div>
            </div>
            {/* Right: controls */}
            <div className="holo-nav-btns" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <button className="holo-ctrl-btn" onClick={goPrev} style={{ background: `${accentColor}12`, border: `1px solid ${accentColor}40`, color: accentColor, padding: '3px 8px', cursor: 'pointer', fontFamily: 'Share Tech Mono, monospace', fontSize: '0.5rem' }}>◀</button>
              <button className="holo-ctrl-btn" onClick={toggleMute} style={{ background: muted ? 'rgba(255,0,85,0.1)' : `${accentColor}12`, border: `1px solid ${muted ? 'rgba(255,0,85,0.4)' : `${accentColor}40`}`, color: muted ? '#FF0055' : accentColor, padding: '3px 8px', cursor: 'pointer', fontFamily: 'Share Tech Mono, monospace', fontSize: '0.5rem' }}>
                {muted ? '🔇' : '🔊'}
              </button>
              <button className="holo-ctrl-btn" onClick={goNext} style={{ background: `${accentColor}12`, border: `1px solid ${accentColor}40`, color: accentColor, padding: '3px 8px', cursor: 'pointer', fontFamily: 'Share Tech Mono, monospace', fontSize: '0.5rem' }}>▶</button>
              {/* EXPAND button — desktop only; hidden on mobile to prevent double-audio glitch */}
              {!isMobile && (
                <button className="holo-ctrl-btn" onClick={() => { sfxClick(); setIsExpanded(true); }} style={{ background: `${accentColor}18`, border: `1px solid ${accentColor}50`, color: accentColor, padding: '3px 10px', cursor: 'pointer', fontFamily: 'Orbitron, monospace', fontSize: '0.4rem', letterSpacing: '0.12em' }}>⛶ EXPAND</button>
              )}
            </div>
          </div>

          {/* ── MAIN CONTENT: side panels + video (desktop) / video only (mobile) ── */}
          <div style={{ display: 'flex', gap: 0 }}>

            {/* Left data panel — hidden on mobile */}
            <div className="holo-side-panel" style={{ width: 80, flexShrink: 0, background: `${accentColor}06`, borderRight: `1px solid ${accentColor}18`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-around', padding: '12px 4px', gap: 8 }}>
              <DataStrip value={String(agentCount)} label="AGENTS" color="#00A6EB" />
              <div style={{ width: '60%', height: 1, background: 'rgba(0,166,235,0.2)' }} />
              <DataStrip value="10" label="DEPTS" color="#00FF88" />
              <div style={{ width: '60%', height: 1, background: 'rgba(0,166,235,0.2)' }} />
              <DataStrip value="$0" label="SALARY" color="#FF0055" />
              <div style={{ width: '60%', height: 1, background: 'rgba(0,166,235,0.2)' }} />
              <DataStrip value="20%" label="BUNDLE" color="#FFB800" />
              <div style={{ width: 2, height: 40, background: 'rgba(0,166,235,0.15)', position: 'relative', overflow: 'hidden', marginTop: 4 }}>
                <div style={{ position: 'absolute', top: `${scanPos}%`, left: 0, right: 0, height: 8, background: 'linear-gradient(to bottom,transparent,#00A6EB,transparent)', transition: 'top 0.03s linear' }} />
              </div>
            </div>

            {/* Video frame */}
            {/* Video frame — click-to-expand disabled on mobile to prevent double-audio glitch */}
            <div style={{ flex: 1, position: 'relative', cursor: isMobile ? 'default' : 'pointer' }} onClick={isMobile ? undefined : () => { sfxClick(); setIsExpanded(true); }}>
              <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
                {currentVideo ? (
                  <video
                    ref={videoRef}
                    key={currentVideo.id}
                    src={currentVideo.video_url}
                    autoPlay loop muted={muted} playsInline preload="none"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
                  />
                ) : (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `radial-gradient(ellipse at center, ${accentColor}10 0%, transparent 70%)` }}>
                    <div style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.8rem', color: `${accentColor}50`, letterSpacing: '0.3em', textAlign: 'center' }}>
                      {activeDept === 'all' ? '◈ INITIALIZING FEED ◈' : `◈ NO ${activeDeptConfig.label} STREAMS ◈`}
                    </div>
                  </div>
                )}
                {/* Scan-line overlay */}
                <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(0,0,0,0.06) 3px,rgba(0,0,0,0.06) 4px)' }} />
                <div style={{ position: 'absolute', left: 0, right: 0, height: 2, top: `${scanPos}%`, background: `linear-gradient(90deg,transparent,${accentColor}60,transparent)`, pointerEvents: 'none', transition: 'top 0.03s linear' }} />
                <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(90deg,rgba(255,0,80,0.04) 0%,transparent 8%,transparent 92%,rgba(0,245,255,0.04) 100%)' }} />
                {/* Mobile mute/unmute floating button */}
                {isMobile && (
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                    style={{
                      position: 'absolute', bottom: 10, right: 10, zIndex: 20,
                      background: muted ? 'rgba(255,0,85,0.18)' : 'rgba(0,166,235,0.18)',
                      border: `1px solid ${muted ? 'rgba(255,0,85,0.6)' : `${accentColor}60`}`,
                      color: muted ? '#FF0055' : accentColor,
                      borderRadius: 4,
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontFamily: 'Share Tech Mono, monospace',
                      fontSize: '0.9rem',
                      lineHeight: 1,
                      backdropFilter: 'blur(8px)',
                      boxShadow: muted ? '0 0 12px rgba(255,0,85,0.3)' : `0 0 12px ${accentColor}40`,
                      animation: 'muteBtnPulse 2.5s ease-in-out infinite',
                      transition: 'all 0.2s ease',
                      minWidth: 38, minHeight: 38,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    aria-label={muted ? 'Unmute video' : 'Mute video'}
                  >
                    {muted ? '🔇' : '🔊'}
                  </button>
                )}
                {/* "CLICK TO EXPAND" hint — desktop only */}
                {!isMobile && (
                  <div style={{ position: 'absolute', bottom: 8, right: 10, fontFamily: 'Share Tech Mono, monospace', fontSize: '0.38rem', color: `${accentColor}50`, letterSpacing: '0.1em', pointerEvents: 'none' }}>CLICK TO EXPAND</div>
                )}
              </div>
            </div>

            {/* Right data panel — hidden on mobile */}
            <div className="holo-side-panel" style={{ width: 80, flexShrink: 0, background: `${accentColor}06`, borderLeft: `1px solid ${accentColor}18`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-around', padding: '12px 4px', gap: 8 }}>
              <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', fontFamily: 'Share Tech Mono, monospace', fontSize: '0.38rem', color: `${accentColor}60`, letterSpacing: '0.12em', maxHeight: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentVideo?.title?.toUpperCase() || 'INTEL BROADCAST'}
              </div>
              <div style={{ width: '60%', height: 1, background: 'rgba(0,166,235,0.2)' }} />
              <DataStrip value={String(videoCount)} label="STREAMS" color="#A855F7" />
              <div style={{ width: '60%', height: 1, background: 'rgba(0,166,235,0.2)' }} />
              <DataStrip value="HD" label="QUALITY" color="#00FF88" />
              <div style={{ width: 2, height: 40, background: 'rgba(0,166,235,0.15)', position: 'relative', overflow: 'hidden', marginTop: 4 }}>
                <div style={{ position: 'absolute', top: `${(scanPos + 50) % 100}%`, left: 0, right: 0, height: 8, background: 'linear-gradient(to bottom,transparent,#A855F7,transparent)', transition: 'top 0.03s linear' }} />
              </div>
            </div>
          </div>

          {/* ── DEPARTMENT FILTER ROW ────────────────────────────────────────── */}
          <div className="holo-dept-row" style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px',
            borderTop: `1px solid ${accentColor}20`,
            background: `linear-gradient(90deg, ${accentColor}06 0%, transparent 50%, ${accentColor}06 100%)`,
            overflowX: 'auto', scrollbarWidth: 'none',
          }}>
            <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.38rem', color: 'rgba(0,166,235,0.4)', letterSpacing: '0.15em', flexShrink: 0, marginRight: 4 }}>FILTER:</span>
            {DEPT_FILTERS.map(dept => {
              const isActive = activeDept === dept.id;
              return (
                <button
                  key={dept.id}
                  className="holo-dept-btn"
                  onClick={() => { sfxClick(); setActiveDept(dept.id); }}
                  onMouseEnter={() => sfxHover()}
                  style={{
                    flexShrink: 0,
                    background: isActive ? `${dept.color}22` : 'transparent',
                    border: `1px solid ${isActive ? dept.color : `${dept.color}30`}`,
                    color: isActive ? dept.color : `${dept.color}70`,
                    padding: '3px 8px',
                    cursor: 'pointer',
                    fontFamily: 'Orbitron, monospace',
                    fontSize: '0.38rem',
                    fontWeight: isActive ? 700 : 400,
                    letterSpacing: '0.12em',
                    boxShadow: isActive ? `0 0 10px ${dept.color}40` : 'none',
                    clipPath: 'polygon(4px 0%,100% 0%,100% calc(100% - 4px),calc(100% - 4px) 100%,0% 100%,0% 4px)',
                  }}
                >
                  {dept.label}
                </button>
              );
            })}
          </div>

          {/* ── BOTTOM HUD BAR ───────────────────────────────────────────────── */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '5px 16px',
            background: `linear-gradient(90deg, ${accentColor}08 0%, ${accentColor}02 50%, ${accentColor}08 100%)`,
            borderTop: `1px solid ${accentColor}20`,
            flexWrap: 'wrap', gap: 4,
          }}>
            {/* Dot indicators */}
            <div className="holo-dot-row" style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
              {videos.slice(0, 12).map((_: any, i: number) => (
                <button
                  key={i}
                  onClick={() => { sfxClick(); setCurrentIdx(i); }}
                  style={{
                    width: i === currentIdx ? 16 : 5, height: 5, borderRadius: 3, border: 'none',
                    background: i === currentIdx ? accentColor : `${accentColor}30`,
                    boxShadow: i === currentIdx ? `0 0 8px ${accentColor}` : 'none',
                    cursor: 'pointer', padding: 0,
                    transition: 'all 0.25s cubic-bezier(0.23,1,0.32,1)',
                  }}
                />
              ))}
              {videos.length > 12 && <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.38rem', color: `${accentColor}50` }}>+{videos.length - 12}</span>}
            </div>
            {/* Title */}
            <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.42rem', color: `${accentColor}60`, letterSpacing: '0.1em', flex: 1, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 12px' }}>
              {currentVideo?.title?.toUpperCase() || ''}
            </div>
            {/* Classification */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#FF0055', boxShadow: '0 0 6px #FF0055', animation: 'sidePulse 1.5s ease-in-out infinite' }} />
              <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.4rem', color: 'rgba(255,0,85,0.6)', letterSpacing: '0.15em' }}>CLASSIFIED</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── EXPANDED FULLSCREEN MODAL ────────────────────────────────────────── */}
      {/* Fullscreen expand modal — desktop only; never rendered on mobile (prevents double-audio) */}
      {isExpanded && !isMobile && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.97)', backdropFilter: 'blur(20px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { sfxClick(); setIsExpanded(false); }}>
          <div style={{ width: '92vw', maxWidth: 1200, position: 'relative', border: `1px solid ${accentColor}60`, boxShadow: `0 0 120px ${accentColor}30` }}
            onClick={e => e.stopPropagation()}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
              {currentVideo && (
                <video ref={expandedRef} key={`exp-${currentVideo.id}`} src={currentVideo.video_url}
                  autoPlay muted={muted} controls preload="none"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
                />
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', background: 'rgba(0,0,0,0.9)', borderTop: `1px solid ${accentColor}25`, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={goPrev} style={{ background: `${accentColor}12`, border: `1px solid ${accentColor}35`, color: accentColor, padding: '6px 14px', cursor: 'pointer' }}><span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.6rem' }}>◀ PREV</span></button>
                <button onClick={goNext} style={{ background: `${accentColor}12`, border: `1px solid ${accentColor}35`, color: accentColor, padding: '6px 14px', cursor: 'pointer' }}><span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.6rem' }}>NEXT ▶</span></button>
              </div>
              <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.55rem', color: `${accentColor}70`, letterSpacing: '0.1em' }}>
                {currentVideo?.title?.toUpperCase() || ''} — {currentIdx + 1}/{videos.length}
              </span>
              <button onClick={() => { sfxClick(); setIsExpanded(false); }} style={{ background: 'rgba(255,0,85,0.12)', border: '1px solid rgba(255,0,85,0.4)', color: '#FF0055', padding: '6px 14px', cursor: 'pointer', fontFamily: 'Orbitron, monospace', fontSize: '0.5rem', letterSpacing: '0.15em' }}>✕ CLOSE</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
